import { z } from 'zod';
import { uuidSchema } from './common';
import { orderDateSchema } from './orders';

export const MAX_PRODUCTION_ORDER_SHEET_LINES = 20;
export const MAX_PRODUCTION_ORDER_SHEET_TEXT_LENGTH = 100;
export const MAX_PRODUCTION_ORDER_SHEET_NOTE_LENGTH = 1000;

const sheetTextSchema = z.string().trim().max(MAX_PRODUCTION_ORDER_SHEET_TEXT_LENGTH);

/** 발주서 품목 한 줄 — 단가를 아직 모르면 null(발주서에는 빈 칸으로 인쇄된다). */
export const productionOrderSheetLineSchema = z.strictObject({
  name: z.string().trim().min(1).max(MAX_PRODUCTION_ORDER_SHEET_TEXT_LENGTH),
  quantity: z.number().int().min(1),
  unitPrice: z.number().int().min(0).nullable(),
});
export type ProductionOrderSheetLine = z.infer<typeof productionOrderSheetLineSchema>;

/** 운영자가 채우는 발주서 칸 — 저장 입력과 기본값(defaults)이 같은 모양을 쓴다. */
export const productionOrderSheetFieldsSchema = z.strictObject({
  supplierName: sheetTextSchema,
  supplierContact: sheetTextSchema,
  supplierPhone: sheetTextSchema,
  supplierEmail: sheetTextSchema,
  dueDate: orderDateSchema.nullable(),
  lines: z.array(productionOrderSheetLineSchema).min(1).max(MAX_PRODUCTION_ORDER_SHEET_LINES),
  note: z.string().trim().max(MAX_PRODUCTION_ORDER_SHEET_NOTE_LENGTH),
});
export type ProductionOrderSheetFields = z.infer<typeof productionOrderSheetFieldsSchema>;

export const saveProductionOrderSheetInputSchema = productionOrderSheetFieldsSchema.extend({
  orderId: uuidSchema,
});
export type SaveProductionOrderSheetInput = z.infer<typeof saveProductionOrderSheetInputSchema>;

/** 저장된 발주서. */
export const productionOrderSheetSchema = productionOrderSheetFieldsSchema.extend({
  id: uuidSchema,
  orderId: uuidSchema,
  updatedAt: z.iso.datetime(),
  updatedByName: z.string().nullable(),
});
export type ProductionOrderSheet = z.infer<typeof productionOrderSheetSchema>;

/** 주문에서 읽어 온 제작 정보(발주서 상단에 인쇄). */
export const productionOrderSheetProductionSchema = z.strictObject({
  productName: z.string(),
  colorName: z.string(),
  /** 일반 견적(인쇄 없는 상품) 주문이면 둘 다 null — 일반 견적 설계 §5-3. */
  printMethodName: z.string().nullable(),
  designName: z.string().nullable(),
});
export type ProductionOrderSheetProduction = z.infer<typeof productionOrderSheetProductionSchema>;

/** 발주서 조회 응답 — 저장 전이면 sheet 는 null 이고 defaults 로 폼을 채운다. */
export const productionOrderSheetViewSchema = z.strictObject({
  sheet: productionOrderSheetSchema.nullable(),
  defaults: productionOrderSheetFieldsSchema,
  production: productionOrderSheetProductionSchema,
});
export type ProductionOrderSheetView = z.infer<typeof productionOrderSheetViewSchema>;
